import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';

export type EntityType = 'candidate' | 'job' | 'match' | 'list' | 'company' | 'contact' | 'task' | 'query' | 'event' | 'workforce';

export interface HistoryEntry {
	id: string;
	user_id: string;
	entity_type: EntityType;
	entity_id: string;
	entity_name: string;
	metadata?: Record<string, any>;
	visited_at: string;
	created_at: string;
}

export interface HistoryMetadata {
	candidateFunction?: string;
	companyName?: string;
	candidateCount?: number;
}

/**
 * Hook to track and retrieve entity visit history
 * Automatically batches updates and handles async operations in the background
 */
export function useEntityHistory() {
	const { user, currentOrganization } = useAuth();

	/**
	 * Track a visit to an entity (non-blocking, fire-and-forget)
	 * Updates visited_at if entity was already visited, or creates new entry
	 */
	const trackVisit = async (
		entityType: EntityType,
		entityId: string,
		entityName: string,
		metadata?: HistoryMetadata
	) => {
		if (!user?.id) return;

		// Fire and forget - don't block navigation (async IIFE)
		(async () => {
			try {
				if (!currentOrganization?.id) return;

				await supabase
					.from('user_history')
					.upsert(
						{
							user_id: user.id,
							entity_type: entityType,
							entity_id: entityId,
							entity_name: entityName,
							metadata: metadata || {},
							visited_at: new Date().toISOString(),
							organization_uuid: currentOrganization.id
						},
						{
							onConflict: 'user_id,entity_type,entity_id',
							ignoreDuplicates: false, // Update visited_at
						}
					)
					.select();
			} catch {
				// Fail silently to not disrupt user experience
			}
		})();
	};

	/**
	 * Get recent history for current user (last 30 entries)
	 * Returns only valid entities (validates existence)
	 */
	const getRecentHistory = async (): Promise<HistoryEntry[]> => {
		if (!user?.id) return [];

		try {
			const { data: history, error } = await supabase
				.from('user_history')
				.select('id, user_id, entity_type, entity_id, entity_name, metadata, visited_at, created_at')
				.eq('user_id', user.id)
				.order('visited_at', { ascending: false })
				.limit(30);

			if (error) throw error;
			if (!history?.length) return [];

			// Batch validate by entity type for efficiency
			const byType = history.reduce((acc, entry) => {
				if (!acc[entry.entity_type]) acc[entry.entity_type] = [];
				acc[entry.entity_type].push(entry);
				return acc;
			}, {} as Record<string, HistoryEntry[]>);

			const validIds = new Set<string>();

			// Validate each entity type in parallel
			await Promise.all(
				Object.entries(byType).map(async ([type, entries]) => {
					const tableName =
						type === 'company'
							? 'companies'
							: type === 'list'
							? 'lists'
							: type === 'match'
							? 'matches'
							: type === 'candidate'
							? 'candidates'
							: type === 'contact'
							? 'contacts'
							: type === 'task'
							? 'tasks'
							: type === 'query'
							? 'saved_queries'
							: type === 'event'
							? 'events'
							: type === 'workforce'
							? 'workforce'
							: 'jobs';
					const ids = (entries as HistoryEntry[]).map((e) => e.entity_id);

					const { data } = await supabase.from(tableName).select('id').in('id', ids);

					data?.forEach((item) => validIds.add(item.id));
				})
			);

			// Filter valid entries
			const valid = history.filter((h) => validIds.has(h.entity_id));
			const invalid = history.filter((h) => !validIds.has(h.entity_id));

			// Cleanup invalid entries in background (fire and forget)
			if (invalid.length > 0) {
				(async () => {
					try {
						await supabase
							.from('user_history')
							.delete()
							.in(
								'id',
								invalid.map((h) => h.id)
							);
					} catch {
						// Silent cleanup failure
					}
				})();
			}

			return valid;
		} catch (error) {
			console.error('Failed to fetch history:', error);
			return [];
		}
	};

	/**
	 * Clear all history for current user
	 */
	const clearHistory = async (): Promise<boolean> => {
		if (!user?.id) return false;

		try {
			const { error } = await supabase.from('user_history').delete().eq('user_id', user.id);

			if (error) throw error;
			return true;
		} catch (error) {
			console.error('Failed to clear history:', error);
			return false;
		}
	};

	return {
		trackVisit,
		getRecentHistory,
		clearHistory,
	};
}

/**
 * Hook to automatically track page visit on mount
 * Use this in detail pages (CandidateProfile, JobDetails, etc.)
 */
export function useTrackPageVisit(
	entityType: EntityType,
	entityId: string | undefined,
	entityName: string | undefined,
	metadata?: HistoryMetadata
) {
	const { trackVisit } = useEntityHistory();

	useEffect(() => {
		if (entityId && entityName) {
			// Track visit after a small delay to ensure page is actually viewed
			const timeout = setTimeout(() => {
				trackVisit(entityType, entityId, entityName, metadata);
			}, 500);

			return () => clearTimeout(timeout);
		}
	}, [entityId, entityName, entityType, metadata, trackVisit]);
}